import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  output,
  signal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  filter,
  finalize,
  map,
  Observable,
  of,
  pairwise,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { ConnectorInfo, ProjectInfo, TableInfo } from '@blaze-shared/api-contracts-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { OAuthService } from '@blaze-shared/auth-data-access';
import {
  ConnectorsApiService,
  ConnectorsCollectionService,
} from '@blaze-shared/connectors-data-access';
import {
  type ConnectorConfiguration,
  type ConnectorOAuthConfig,
  ConnectorSetupPhase,
  type ConnectorTemplate,
  GenericOAuthAuthorizationMessage,
} from '@blaze-shared/connectors-domain';
import { TablesApiService } from '@blaze-shared/datasets-data-access';
import { LocalStorageService, NotificationService } from '@blaze-shared/platform-data-access';
import { ProjectsCollectionService } from '@blaze-shared/projects-data-access';
import { SelectOption } from '@blaze-shared/shared-util';

import { DataConnectorsStore } from '../+state/data-connectors.store';
import { ConnectorForm } from '../dialogs/typeform-connector-dialog/typeform-connector-dialog.models';
import { ConnectorsService } from '../services/connectors.service';

import { ConnectorConfigurationBase } from './base-connector-dialog.models';

@UntilDestroy()
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class BaseConnectorDialogComponent<
  T extends ConnectorConfigurationBase = ConnectorConfigurationBase,
> {
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly connectorsApiService = inject(ConnectorsApiService);
  protected readonly connectorsCollService = inject(ConnectorsCollectionService);
  protected readonly connectorsService = inject(ConnectorsService);
  protected readonly dataConnectorsStore = inject(DataConnectorsStore);
  protected readonly localStorageService = inject(LocalStorageService);
  protected readonly notificationService = inject(NotificationService);
  protected readonly oAuthService = inject(OAuthService);
  protected readonly projectContextFacade = inject(ProjectContextFacade);
  protected readonly projectsCollService = inject(ProjectsCollectionService);
  protected readonly tablesApiService = inject(TablesApiService);

  showDatasets = output<ConnectorConfiguration>();

  @Input() config?: ConnectorOAuthConfig;
  @Input() connector?: ConnectorConfiguration;
  @Input() template?: ConnectorTemplate;

  protected readonly SetupPhase = ConnectorSetupPhase;

  protected availableProjects$$ = new BehaviorSubject<ProjectInfo[]>([]);
  protected availableTables$$ = new BehaviorSubject<TableInfo[]>([]);
  protected setupPhase$$ = new BehaviorSubject<ConnectorSetupPhase>(this.SetupPhase.Init);
  protected loadingDialog$$ = new BehaviorSubject<boolean>(false);

  protected readonly accountId: string = '';
  protected connectionSubscription?: Subscription;

  abstract serviceName: string;
  abstract connectorForm: FormGroup;

  popupRef: WindowProxy | null = null;
  connectorId = '';
  authState = '';
  authCode = '';
  authorizationUrl = '';
  appInstalled = signal(false);

  availableProjectOptions$: Observable<SelectOption<string>[]>;
  availableTablesOptions$: Observable<SelectOption<string>[]>;
  setupPhase$ = this.setupPhase$$.asObservable();
  loadingDialog$ = this.loadingDialog$$.asObservable();
  projectId$ = this.dataConnectorsStore.projectId$;
  coupledConnector$ = this.dataConnectorsStore.coupledConnector$;

  get isAuthorizeButtonVisible(): boolean {
    return !!this.connector && this.setupPhase$$.value === ConnectorSetupPhase.ConnectorCreation;
  }

  get isCopyConfigurationButtonVisible(): boolean {
    return this.setupPhase$$.value === ConnectorSetupPhase.ConnectorCreation;
  }

  get isFormButtonVisible(): boolean {
    return (
      !!this.connectorId &&
      !!this.authState &&
      this.setupPhase$$.value === ConnectorSetupPhase.Authorization
    );
  }

  get isSaveButtonVisible(): boolean {
    return !!this.template?.id && this.setupPhase$$.value === ConnectorSetupPhase.ConnectorCreation;
  }

  get saveButtonLabel(): string {
    return this.connector ? 'save connector' : 'create connector';
  }

  protected constructor() {
    this.accountId = this.localStorageService.getAccountId();
    if (!this.accountId) {
      throw new Error('Could not get account id');
    }

    this.availableProjectOptions$ = this.availableProjects$$.pipe(
      map((projects) =>
        projects.map<SelectOption<string>>((project) => ({
          label: project.name,
          value: project.id,
        })),
      ),
    );

    this.availableTablesOptions$ = this.availableTables$$.pipe(
      map((tables) =>
        tables.map<SelectOption<string>>((table) => ({ label: table.name, value: table.id })),
      ),
    );
  }

  abstract saveConnector(): void;

  abstract getConfiguration(connector?: ConnectorConfiguration): T;

  abstract fillForm(configuration: T): void;

  connect(): void {
    this.setupPhase$$.next(ConnectorSetupPhase.PopupOpened);

    if (this.connectionSubscription) {
      this.connectionSubscription.unsubscribe();
      this.connectionSubscription = undefined;
    }

    if (!this.authorizationUrl) {
      throw new Error('Could not find authorization url');
    }

    this.connectionSubscription = this.oAuthService
      .openPopup(this.authorizationUrl, {
        width: '700px',
        height: '500px',
        onClose: () => {
          if (this.setupPhase$$.value === ConnectorSetupPhase.PopupOpened) {
            this.setupPhase$$.next(ConnectorSetupPhase.Authorization);
          }
        },
      })
      .pipe(
        map((ref) => {
          this.popupRef = ref.popup;
          this.cdr.detectChanges();
          return ref;
        }),
        switchMap((ref) =>
          this.oAuthService.getAuthorizationMessage<GenericOAuthAuthorizationMessage>(ref),
        ),
        map((message) => this.filterMessage(message, this.config)),
        tap((message) => {
          this.setupPhase$$.next(ConnectorSetupPhase.Install);
          this.loadingDialog$$.next(true);
          this.authState = message.state;
          this.authCode = message.code;
          this.appInstalled.set(false);
        }),
        switchMap((message) => {
          return this.connectorsApiService.postInstallServiceApp(this.serviceName, message).pipe(
            map(() => message),
            tap(() => {
              this.appInstalled.set(true);
            }),
          );
        }),
        untilDestroyed(this),
        finalize(() => {
          this.popupRef = null;
        }),
      )
      .subscribe({
        next: () => {
          this.setupPhase$$.next(ConnectorSetupPhase.ConnectorCreation);
          this.loadingDialog$$.next(false);
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.handleErrors(error);
          this.setupPhase$$.next(ConnectorSetupPhase.Authorization);
          this.loadingDialog$$.next(false);
        },
      });
  }

  focusOpenedWindow(): void {
    if (this.popupRef) {
      this.popupRef.focus();
    }
  }

  goToAuth(options?: { reauthorize?: boolean }): void {
    if (options?.reauthorize) {
      this.appInstalled.set(false);
      this.authState = '';
    }
    this.setupPhase$$.next(ConnectorSetupPhase.Authorization);
  }

  goToForm(): void {
    this.setupPhase$$.next(ConnectorSetupPhase.ConnectorCreation);
  }

  copyConfiguration(connector?: ConnectorConfiguration): void {
    const configuration = this.getConfiguration(connector);
    this.fillForm(configuration);
  }

  copyFormValueToClipboard(field: keyof ConnectorForm): void {
    const value = this.connectorForm.value[field];

    if (!value || typeof value !== 'string') {
      return;
    }
    navigator.clipboard.writeText(value).then(() => {
      this.notificationService.success(`Copied to clipboard`, false, 3000);
    });
  }

  protected getAuthorizationUrl(): Observable<{ url: string }> {
    return this.connectorsApiService.getServiceAuthorizeUrl(this.serviceName);
  }

  protected initUseWebhookCleanup(connectorForm: FormGroup): Subscription {
    return connectorForm.controls.useWebhook.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((useWebhook) => {
        if (!useWebhook) {
          this.connectorForm.patchValue({
            webhookId: '',
          });
          return;
        }

        const configuration = this.getConfiguration(this.connector);
        if (this.connectorForm.value.webhookId) {
          return;
        }

        if ('id' in configuration && configuration.id) {
          this.connectorForm.patchValue({
            webhookId: configuration.id,
          });
        }
      });
  }

  protected loadProjects(): Observable<ProjectInfo[]> {
    return this.projectsCollService
      .getWithQuery({ accountId: this.accountId, skipProviders: true })
      .pipe(
        untilDestroyed(this),
        take(1),
        catchError((error) => {
          this.handleErrors(error);
          return of([]);
        }),
      );
  }

  protected loadTables(projectId: string): Observable<TableInfo[]> {
    if (!projectId) {
      return of([]);
    }

    return this.tablesApiService.getTables(projectId).pipe(
      untilDestroyed(this),
      catchError((error) => {
        this.handleErrors(error);
        return of([]);
      }),
    );
  }

  protected withOAuth(): void {
    this.setupPhase$
      .pipe(
        untilDestroyed(this),
        pairwise(),
        filter(
          ([prevPhase, currPhase]) =>
            (prevPhase === ConnectorSetupPhase.Init ||
              (prevPhase !== ConnectorSetupPhase.Authorization &&
                prevPhase !== ConnectorSetupPhase.PopupOpened)) &&
            currPhase === ConnectorSetupPhase.Authorization,
        ),
        tap(() => {
          this.loadingDialog$$.next(true);
        }),
        concatMap(() => this.getAuthorizationUrl()),
      )
      .subscribe({
        next: ({ url }) => {
          this.authorizationUrl = this.prepareAuthorizationUrl(url, this.authState);
          this.loadingDialog$$.next(false);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.handleErrors(error);
        },
      });
  }

  protected handleErrors(error: any): void {
    this.notificationService.showError(error);
  }

  protected saveOrUpdateConnector(
    configuration: ConnectorConfiguration,
    connectorId?: string,
  ): Observable<ConnectorInfo | null> {
    return (
      connectorId
        ? this.connectorsCollService.update({ ...configuration }, { correlationId: connectorId })
        : this.connectorsCollService.add(configuration)
    ).pipe(
      catchError((error) => {
        this.notificationService.showError(error);
        return of(null);
      }),
    );
  }

  private prepareAuthorizationUrl(url: string, authState: string): string {
    if (!authState) {
      return url;
    }

    const ourl = new URL(url);
    ourl.searchParams.set('state', authState);
    return ourl.toString();
  }

  private filterMessage(
    message: GenericOAuthAuthorizationMessage,
    config?: ConnectorOAuthConfig,
  ): GenericOAuthAuthorizationMessage {
    const filteredMessage: GenericOAuthAuthorizationMessage = {
      code: message.code,
      state: message.state,
    };

    if (config?.allowedInstallFields) {
      config.allowedInstallFields.forEach((key) => {
        if (message[key]) {
          filteredMessage[key] = message[key];
        }
      });
    }

    return filteredMessage;
  }
}
